import React from 'react';
import Style from './total.module.css';
import { CurrencyInputLabel, TooltipHelp } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { InputMaxPrice } from '../../models/rateModel';
import { compute_sum_float } from '../../pages/rates/utils';
import SellingPrice from './sellingPrice/selling-price';
import i18n from 'i18next';

export default function Total({
    currency,
    fees,
    price,
    includeTaxInPrice,
    commission,
    commissionTaxless,
    displayExclVat,
    type,
    onPriceChange,
    disabled,
    settings,
    rate,
    event,
    loadingSettings,
    rateLimit,
    form,
    formErrors,
    onDebouncePriceChange
}) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const addFeesToPrice = (allFees = [], priceValue) => {
        return allFees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), parseFloat(priceValue || 0));
    };
    const taxlessFees = React.useMemo(
        () =>
            fees.map(({ amount }) => {
                return { amount: includeTaxInPrice(false, amount) };
            }),
        [includeTaxInPrice, fees]
    );
    const computedPrice = React.useMemo(() => addFeesToPrice(fees, price), [fees, price, addFeesToPrice]);
    const taxlessPrice = React.useMemo(() => addFeesToPrice(taxlessFees, includeTaxInPrice(false, price)), [addFeesToPrice, includeTaxInPrice, taxlessFees, price]);

    const _totalForOrga = React.useMemo(() => {
        return Math.round((computedPrice - commission) * 100) / 100;
    }, [computedPrice, commission]);

    const _totalTaxless = React.useMemo(() => {
        return Math.round((taxlessPrice - commissionTaxless) * 100) / 100;
    }, [taxlessPrice, commissionTaxless]);

    return (
        <div>
            <TotalBuyer
                currency={currency}
                locale={locale}
                fees={fees}
                displayExclVat={displayExclVat}
                type={type}
                onPriceChange={onPriceChange}
                disabled={disabled}
                onDebouncePriceChange={onDebouncePriceChange}
                formErrors={formErrors}
                form={form}
                event={event}
                rateLimit={rateLimit}
                loadingSettings={loadingSettings}
                rate={rate}
                settings={settings}
                computedPrice={computedPrice}
                taxlessPrice={taxlessPrice}
                includeTaxInPrice={includeTaxInPrice}
            />
            <div className={`${Style['amount']} ${Style['commission-container']}`}>
                <div className={Style['amount-label-container']}>
                    <span className={Style['amount-label']}>{type === 'open' ? t('ticket.rates.infos.commissionMin') : t(`ticket.rates.infos.commission`)}</span>
                    <TooltipHelp triggerOnHover title={t('ticket.rates.infos.commissionTooltip')} />
                </div>
                <div className={Style['amount-commission-container']}>
                    <CurrencyInputLabel
                        {...(displayExclVat && { label: t('ticket.rates.infos.priceMinShort') })}
                        currency={currency}
                        value={+computedPrice === 0 ? 0 : commission || 0}
                        disabled
                        locale={locale}
                    />
                    {displayExclVat && (
                        <CurrencyInputLabel
                            label={t('ticket.rates.infos.priceNoTaxesShort')}
                            currency={currency}
                            value={+taxlessPrice === 0 ? 0 : commissionTaxless || 0}
                            disabled
                            locale={locale}
                        />
                    )}
                </div>
            </div>
            <div className={Style['amount']}>
                <div className={Style['amount-label-container']}>
                    <span className={Style['amount-label']}>{t(`ticket.rates.infos.total.total-organizer${type === 'open' ? '-open' : ''}`)}</span>
                    <TooltipHelp triggerOnHover title={t('ticket.rates.infos.total.tooltip')} />
                </div>
                <div className={Style['amount-commission-container']}>
                    <CurrencyInputLabel
                        {...(displayExclVat && { label: t('ticket.rates.infos.priceMinShort') })}
                        onChange={onPriceChange('total-orga', 'with-tax')}
                        max={Math.max(InputMaxPrice, _totalForOrga)}
                        currency={currency}
                        value={_totalForOrga}
                        disabled={disabled}
                        locale={locale}
                    />
                    {displayExclVat && (
                        <CurrencyInputLabel
                            label={t('ticket.rates.infos.priceNoTaxesShort')}
                            onChange={onPriceChange('total-orga', 'without-tax')}
                            max={Math.max(InputMaxPrice, _totalTaxless)}
                            currency={currency}
                            value={_totalTaxless}
                            disabled={disabled}
                            locale={locale}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function TotalBuyer({
    currency,
    fees,
    displayExclVat,
    type,
    onPriceChange,
    disabled,
    onDebouncePriceChange,
    formErrors,
    form,
    event,
    rateLimit,
    loadingSettings,
    rate,
    settings,
    computedPrice,
    taxlessPrice,
    includeTaxInPrice,
    locale
}) {
    const { t } = useTranslation();

    return (
        <>
            {!fees.length ? (
                <SellingPrice
                    totalBuyerMode
                    onDebouncePriceChange={onDebouncePriceChange}
                    formErrors={formErrors}
                    form={form}
                    event={event}
                    rateLimit={rateLimit}
                    loadingSettings={loadingSettings}
                    rate={rate}
                    settings={settings}
                    includeTaxInPrice={includeTaxInPrice}
                />
            ) : (
                <div className={Style['amount-buyer']}>
                    <div>
                        <span className={Style['amount-label-buyer']}>{t(`ticket.rates.infos.total.total-buyer${type === 'open' ? '-open' : ''}`)}</span>
                        <TooltipHelp triggerOnHover title={t('ticket.rates.infos.total.tooltip')} />
                    </div>
                    <div className={Style['amount-commission-container']}>
                        <CurrencyInputLabel
                            {...(displayExclVat && { label: t('ticket.rates.infos.priceMinShort') })}
                            onChange={onPriceChange('total-buyer', 'with-tax')}
                            max={Math.max(InputMaxPrice, computedPrice)}
                            currency={currency}
                            value={computedPrice}
                            disabled={disabled}
                            locale={locale}
                        />
                        {displayExclVat && (
                            <CurrencyInputLabel
                                label={t('ticket.rates.infos.priceNoTaxesShort')}
                                onChange={onPriceChange('total-buyer', 'without-tax')}
                                max={Math.max(InputMaxPrice, taxlessPrice)}
                                currency={currency}
                                value={taxlessPrice}
                                disabled={disabled}
                                locale={locale}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
